import { ShowDialogProps } from '@lovoldsystem/ui'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateComponentsCache } from 'src/api/components'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateDeviation, useAddDeviation } from 'src/api/deviation'
import { invalidateSchemaForm } from 'src/api/form-schema'
import { invalidateMainComponentCache } from 'src/api/main-component'
import { invalidatePositionCache } from 'src/api/positions'
import { DeviationStepperForm } from 'src/forms/DeviationStepperForm/DeviationStepperForm'
import { IDeviationType } from 'src/generated-types/deviation-type'
import { IFormSchemaReportDeviationDto } from 'src/generated-types/form-schema'

export interface IChecklistFieldMetadata {
  formSchemaReportId?: number
  fieldId?: string
  itemId?: string
  itemQuestion?: string
}

interface CreateDeviationDialogProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  scheduledTaskId?: EntityId
  deviationType?: IDeviationType
  deviationTypeFromParent?: IDeviationType
  checklistFieldMetadata?: IChecklistFieldMetadata
  returnWithoutSaving?: boolean
  onDeviationCreated?: (deviationDto: IFormSchemaReportDeviationDto) => void
  isDisableWizard?: boolean
  mainComponentId?: EntityId
}

const CreateDeviationDialog: React.FC<CreateDeviationDialogProps> = ({
  pop,
  customerId,
  localityId,
  scheduledTaskId,
  deviationType = IDeviationType.Mooring,
  deviationTypeFromParent,
  checklistFieldMetadata,
  returnWithoutSaving = false,
  onDeviationCreated,
  isDisableWizard = false,
  mainComponentId,
}) => {
  const { t } = useTranslation(['deviation', 'common'])

  const mutation = useAddDeviation(customerId)
  const { mooringId } = useParams()

  const onSubmit = async form => {
    form.localityId = localityId
    form.type = form.deviationTypeFromParent ?? form.type
    if (form.type === IDeviationType.Equipment || form.type === IDeviationType.MainComponent) delete form.localityId

    if (mainComponentId) {
      form.mainComponentId = mainComponentId
    }

    if (checklistFieldMetadata) {
      form.formSchemaReportId = checklistFieldMetadata.formSchemaReportId
      form.checklistFieldId = checklistFieldMetadata.fieldId
      form.checklistItemId = checklistFieldMetadata.itemId
    }

    // If returnWithoutSaving is true, return the deviation data without saving
    if (returnWithoutSaving && onDeviationCreated) {
      const formSchemaReportDeviation: IFormSchemaReportDeviationDto = {
        deviation: {
          ...form,
          tempId: uuidv4(),
        },
        checklistFieldId: checklistFieldMetadata?.fieldId,
        checklistItemId: checklistFieldMetadata?.itemId,
      }

      onDeviationCreated(formSchemaReportDeviation)
      toast.success(t('deviationAddedToChecklist', { ns: 'deviation' }))
      pop()
      return
    }

    await mutation.mutateAsync(form, {
      onSuccess: async () => {
        if (mooringId) {
          invalidatePositionCache.getPositions(customerId as EntityId, localityId as EntityId, mooringId as EntityId)
        }
        invalidateDeviation.invalidateAllDeviations(customerId as EntityId, localityId)
        invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
        invalidationActivityLogCache.getCageActivityLogs()
        invalidateCustomer.getCustomersActivityCounts(customerId)
        invalidateComponentsCache.getComponents(customerId, form.positionId)
        invalidateMainComponentCache.getMainComponents(customerId as EntityId, localityId as EntityId)
        invalidateSchemaForm.getFormSchemaReports(customerId)
        toast.success(t('addedSuccessfully', { ns: 'common' }))
        pop()
      },
    })
  }

  return (
    <DeviationStepperForm
      title={t('addNewDeviation', { ns: 'deviation' })}
      isLoading={returnWithoutSaving ? false : mutation.isLoading}
      onSubmit={onSubmit}
      type={deviationType}
      onCancel={() => pop()}
      customerId={customerId}
      localityId={localityId}
      isDisableWizard={isDisableWizard}
      scheduledTaskId={scheduledTaskId}
      lgModal={true}
      deviationTypeFromParent={deviationTypeFromParent}
    />
  )
}

export default CreateDeviationDialog
